import { useEffect, useState, useCallback } from "react";
import { Eye, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { inputCls, Label } from "./shared";

export function ScoutEventPanel() {
  const [ev, setEv] = useState(null);
  const [hours, setHours] = useState(168);
  const [mult, setMult] = useState(2);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => api.get("/admin/scout-event").then((r) => setEv(r.data)).catch(() => setEv({ active: false })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);

  const start = async () => {
    if (!(hours > 0)) return toast.error("Enter a duration greater than 0");
    setBusy(true);
    try { const r = await api.post("/admin/scout-event", { hours: Number(hours), multiplier: Number(mult) }); setEv(r.data); toast.success(`${mult}× faster scouting event started`); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/scout-event", { hours: 0 }); setEv(r.data); toast.success("Scouting event stopped"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  let remaining = "";
  if (ev?.active && ev.active_until) {
    const ms = new Date(ev.active_until).getTime() - now;
    if (ms > 0) {
      const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
      remaining = `${d > 0 ? d + "d " : ""}${h}h ${m}m`;
    }
  }

  return (
    <div data-testid="scout-event-panel" className={`mb-6 border p-4 rounded-sm ${ev?.active ? "border-sky-500/60 bg-sky-950/20" : "border-amber-900/30 bg-zinc-950"}`}>
      <div className="flex items-center gap-2 mb-3">
        <Eye size={16} className="text-sky-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Faster Scouting Event</h3>
        {ev?.active && (
          <span data-testid="scout-event-active-badge" className="ml-auto flex items-center gap-1.5 text-xs text-sky-300 font-semibold">
            <Clock size={13} /> {ev.multiplier}× active · {remaining}
          </span>
        )}
      </div>
      {ev?.active ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-zinc-400 flex-1 min-w-[200px]">Gladiator potential is revealed at <span className="text-sky-300 font-semibold">{ev.multiplier}×</span> speed until the timer ends — this stacks on top of VIP and alliance library bonuses.</p>
          <button data-testid="scout-event-stop-btn" onClick={stop} disabled={busy}
            className="px-4 py-2 border border-red-900/50 text-red-300 hover:border-red-500 text-sm rounded-sm disabled:opacity-50">End Event Now</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <p className="text-sm text-zinc-500 w-full mb-1">Speed up potential scouting platform-wide. Stacks multiplicatively with the alliance Library. Default is a full week (168h).</p>
          <div className="w-28"><Label>Duration (hours)</Label>
            <input data-testid="scout-event-hours" type="number" min="1" step="1" className={inputCls} value={hours} onChange={(e) => setHours(e.target.value)} /></div>
          <div className="w-28"><Label>Multiplier</Label>
            <select data-testid="scout-event-mult" className={inputCls} value={mult} onChange={(e) => setMult(Number(e.target.value))}>
              {[2, 3, 4, 5].map((m) => <option key={m} value={m}>{m}×</option>)}
            </select></div>
          <button data-testid="scout-event-start-btn" onClick={start} disabled={busy}
            className="px-5 py-2 bg-sky-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-sky-400 disabled:opacity-50 rounded-sm">Start Event</button>
        </div>
      )}
    </div>
  );
}
